import { API_URL } from "./sys";
import {
  AUTH_TOKEN_PATH,
  clearPartnerAccessToken,
  getPartnerAccessToken,
  setPartnerAccessToken,
} from "./partnerAuth";

export const FetchRetailers = `${API_URL}/retailers`;
export const SendOTP = `${API_URL}/otp/send`;
export const VerifyOTPs = `${API_URL}/otp/verify`;

function buildAuthHeaders(extra?: Record<string, string>): HeadersInit {
  const storedUserData = localStorage.getItem("userData");
  const parsedUserData = storedUserData ? JSON.parse(storedUserData) : null;
  const userId =
    parsedUserData?.partner_id ||
    parsedUserData?.user_id ||
    localStorage.getItem("USER_ID") ||
    "";
  const loginRole = parsedUserData?.partner_type || parsedUserData?.role || "";

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    userId,
    userRole: loginRole,
    ...extra,
  };

  const token = getPartnerAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function handleAuthFailure(status: number): void {
  if (status === 401 || status === 403) {
    clearPartnerAccessToken();
  }
}

/**
 * Mint a short-lived partner JWT for WebView launch userId.
 * Call once at app start before authenticated API calls.
 * Token is stored in memory only.
 */
export const fetchPartnerAccessToken = async (userId: string): Promise<void> => {
  if (!userId) {
    throw new Error("userId is required to fetch partner access token");
  }

  const response = await fetch(`${API_URL}${AUTH_TOKEN_PATH}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-User-ID": userId,
    },
    body: JSON.stringify({ userId }),
  });

  if (!response.ok) {
    handleAuthFailure(response.status);
    const errorText = await response.text();
    throw new Error(`HTTP error ${response.status}: ${errorText}`);
  }

  const data = await response.json();
  if (!data?.accessToken) {
    throw new Error("Token response missing accessToken");
  }

  setPartnerAccessToken(data.accessToken, Number(data.expiresIn) || 3600);
};

export const get = async (endpoint: string, params?: Record<string, string>): Promise<any> => {
  const url = new URL(`${API_URL}${endpoint}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) =>
      url.searchParams.append(key, value)
    );
  }

  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders(),
  });

  if (response.ok) {
    if (response.status === 204) {
      console.log("No content returned from the server.");
      return null;
    } else if (response.status === 200) {
      const data = await response.json();
      return data;
    } else {
      const errorText = await response.text();
      throw new Error(`HTTP error ${response.status}: ${errorText}`);
    }
  } else {
    handleAuthFailure(response.status);
    const errorText = await response.text();
    throw new Error(`HTTP error ${response.status}: ${errorText}`);
  }
};

export const post = async (endpoint: string, data: any): Promise<any> => {
  const storedUserData = await localStorage.getItem("userData");
  const parsedUserData = storedUserData ? JSON.parse(storedUserData) : null;
  console.log("Parsed User Data:", parsedUserData);

  const response = await fetch(`${API_URL}${endpoint}`, {
    method: "POST",
    headers: buildAuthHeaders(),
    body: data,
  });

  if (response.status === 401 || response.status === 403) {
    handleAuthFailure(response.status);
  }

  const responseData = await response.json();

  if (response.ok) {
    return responseData;
  } else {
    if (
      responseData.message === "Failed to create retailer" &&
      response.status === 500
    ) {
      window.alert(
        "A survey is already under progress/completed for this user, please contact your Admin for more info"
      );
      return;
    } else if (response.status === 409) {
      return { ...responseData, status: response.status };
    } else {
      window.alert("Something went Wrong!");
      return;
    }
  }
};

export const put = async (endpoint: string, data: any): Promise<any> => {
  const response = await fetch(`${API_URL}${endpoint}`, {
    method: "PUT",
    headers: buildAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401 || response.status === 403) {
    handleAuthFailure(response.status);
  }

  const responseData = await response.json();

  if (response.ok) {
    return responseData;
  } else {
    if (
      responseData.message === "Failed to create retailer" &&
      response.status === 500
    ) {
      window.alert(
        "A survey is already under progress/completed for this user, please contact your Admin for more info"
      );
      return;
    } else if (response.status === 409) {
      return { ...responseData, status: response.status };
    } else {
      window.alert("Something went Wrong!");
      return;
    }
  }
};
